<template>
  <div class="station-optimiser-sheet-list-container">    
    <div :class="['station-optimiser-sheet-list', {'station-optimiser-sheet-list-with-footer': displayFooterOptions}]">

      <div class="station-schedule-searchbar-container" v-if="displaySearchBar">
        <label class="align-middle m-0">
          <i class="far fa-search station-schedule-search-icon align-middle"></i>
          <input type="text" class="form-control station-schedule-search-bar" v-model="searchText" placeholder="Search" />
          <button class="btn bg-transparent schedule-search-cancel-button" v-if="searchText" @click.prevent="clearSearch">
            <i class="fa fa-times"></i>
          </button>
        </label>
      </div>

      <div class="text-center text-danger" v-if="sheetScheduleError">{{sheetScheduleError}}</div>
      <h3 class="text-center" v-if="searchText">Search Results for '{{searchText}}'</h3>
      <Spinner v-if="scheduleLoading" />
      <div v-for="group of filteredScheduleGroupList" :key="`group-${group.key}`" class="station-optimiser-sheet-draggable-container">
        <div :class="['station-schedule-header']" :style="{'color': group.colour}">
          {{group.header}}
          <div class="station-schedule-header-badge">{{group.groupCount}}</div>
        </div>

        <OptimiserSheetRow
          :sheet="sheet"
          :ref="`sheet-${sheet.id}`"
          :colourHighlight="group.colour"
          @click="sheetClicked(sheet)"
          v-for="sheet of group.sheetList"
          :key="`sheet-${sheet.id}`" 
        />
      </div>

      <div v-if="isEmptySchedule" class="text-center">
        <h2>{{emptyScheduleText}}</h2>
      </div>
      <div v-if="filteredScheduleGroupList.length == 0" class="text-center">
        <h2>No sheets in the search result</h2>
      </div>
    </div>

    <div class="station-schedule-filterbar" v-if="displayFooterOptions">
      <div class="row no-gutters">
        <div class="offset-8 col-4 px-2">
          <div class="row no-gutters station-schedule-page-area">
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage-1)" :disabled="scheduleCurrentPage == 0"><i class="far fa-angle-left"></i></button>
            </div>
            <div class="col-6 station-schedule-page-area-display">
              {{scheduleCurrentPage+1}} / {{schedulePageCount}}
            </div>
            <div class="col-3">
              <button class="btn btn-block btn-default" @click.prevent="setPageIndex(scheduleCurrentPage+1)" :disabled="scheduleCurrentPage == schedulePageCount-1"><i class="far fa-angle-right"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.station-optimiser-sheet-list-container {
  width: 100%;
  height: 100%;
  position: relative;
  font-size: 13px;
}
.station-optimiser-sheet-list {
  position: absolute;
  width: 100%;
  top: 0;
  bottom: 0;
  overflow-y:scroll;  
}
</style>


<script>
import _ from 'lodash'
import moment from 'moment'

import Utils from '@/utils/utils'

import OptimiserSheetRow from './optimiserSheetRow'

export default {
  name: 'OptimiserSheetList',
  components: {
    OptimiserSheetRow
  },
  props: {
    displaySearchBar: {
      type: Boolean,
      default: true
    },
    displayFooterOptions: {
      type: Boolean,
      default: true
    },
    emptyScheduleText: {
      type: String,
      default: 'No sheets in the schedule for this station'
    },
  },
  data() {
    return {
      searchText: '',
      scheduleLoading: true,
      sheetScheduleError: '',
      sheetScheduleData: {},

      schedulePageCount: 0,
      scheduleOffset: 0,
      schedulePageLength: 50,
      scheduleCurrentPage: 0,
      scheduleCurDraw: 0,
      scheduleRecvDraw: 0,
    }
  },
  watch: {
    searchText: function() {
      this.scheduleCurrentPage = 0
      this.scheduleLoading = true
      this.debouncedSearchSheetScheduleList()
    }
  },
  computed: {
    isEmptySchedule() {
      return (!this.sheetScheduleData.groupList || this.sheetScheduleData.groupList.length == 0)      
    },
    filteredScheduleGroupList() {
      var currentDate = moment().local()
      let urgentColour = '#EB4D4B'
      let todayColour = '#FBC530'
      let goodColour = '#4CD137'

      if(!this.sheetScheduleData.groupList)
        return []

      var outputList = this.sheetScheduleData.groupList.map(scheduleGroup => {
        
        var groupColour = '#4CD137'
        var groupHeader = ''

        if(scheduleGroup.options.isNCRGroup) {
          groupColour = urgentColour
          groupHeader = 'NCR'
        }
        else if(scheduleGroup.options.isOverdueGroup) {
          groupColour = urgentColour
          groupHeader = 'OVERDUE'
        }
        else {
          let groupDate = moment(scheduleGroup.options.date).local()
          
          // Today?
          if(groupDate.isSame(currentDate, 'day')) {
            groupColour = todayColour
            groupHeader = `TODAY - ${groupDate.format('Do MMM')}`
          } else {
            var dayName = groupDate.format('dddd')
            if(groupDate.isSame(currentDate.clone().add(1, 'days'), 'day'))
              dayName = 'Tomorrow'

            groupColour = goodColour
            groupHeader = `${dayName} - ${groupDate.format('Do MMM')}`
          }
        }

        return {
          header: groupHeader.toUpperCase(),
          colour: groupColour,
          key: scheduleGroup.key,
          groupCount: scheduleGroup.groupCount,
          sheetList: scheduleGroup.sheetList
        }
      })

      return outputList
    }
  },
  created() {
    this.debouncedSearchSheetScheduleList = _.debounce(this.requestSheetScheduleList, 250)
    this.debouncedNavigateSheetScheduleList = _.debounce(this.requestSheetScheduleList, 100)
  },
  mounted() {
    this.scheduleLoading = true
    this.requestSheetScheduleList()

    // Subscribe to the generic sheet schedule list (virtual endpoint, not actual one)
    // Any updates to sheet allocations will refresh this endpoint
    this.subscribeEndpoint('/production/sheetschedule', () => {
      this.debouncedSearchSheetScheduleList()
    })
    this.subscribeEndpoint('/production/sheetlayout', (data) => {
      this.debouncedSearchSheetScheduleList()      
    })
  },
  beforeDestroy() {
    this.unsubscribeEndpoint('/production/sheetschedule')
    this.unsubscribeEndpoint('/production/sheetlayout')
  },
  methods: {
    clearSearch() {
      this.searchText = ''
    },
    sheetClicked(sheet) {
      let sheetComponent = this.$refs[`sheet-${sheet.id}`][0]

      this.$emit('sheetClick', { sheet: sheet, target: sheetComponent.$el })
    },
    setPageIndex(index) {
      if(index < 0 || index >= this.schedulePageCount)
        return

      this.scheduleLoading = true
      this.scheduleCurrentPage = index
      this.debouncedNavigateSheetScheduleList()
    },

    // Load the sheet schedule list
    requestSheetScheduleList() {

      // Note: We don't set loading here explicitly as this function will be called very often due to sheet update notifications via socket service
      // Only set loading when explicitly requested (search, page change, etc)
      this.scheduleOffset = this.scheduleCurrentPage * this.schedulePageLength
      this.scheduleListRequiresUpdate = false

      var params = {
        start: this.scheduleOffset,
        length: this.schedulePageLength,
        draw: this.scheduleCurDraw++,
        searchString: this.searchText != '' ? encodeURIComponent(this.searchText) : null,
      }

      this.sheetScheduleError = ''
      this.$apiService.glassProductionStationService.getSheetScheduleForStation(this.$route.params.id, params)
      .then(response => {
        // Discard results if the data is older than newer data we have
        if(parseInt(response.data.draw) >= this.scheduleRecvDraw) {
          this.sheetScheduleData = response.data

          this.schedulePageCount = Math.ceil(this.sheetScheduleData.totalSheets / this.schedulePageLength)

          this.scheduleCurrentPage = this.scheduleOffset / this.schedulePageLength
          this.scheduleCurrentPage = Math.min(this.scheduleCurrentPage, this.schedulePageCount-1)
          this.scheduleCurrentPage = Math.max(this.scheduleCurrentPage, 0)

          this.scheduleRecvDraw = parseInt(response.data.draw)
          this.scheduleCurDraw = Math.max(this.scheduleCurDraw, this.scheduleRecvDraw)
        }
      })
      .catch(err => {
        this.sheetScheduleError = 'Error loading sheet schedule'
      })
      .finally(() => {
        this.scheduleLoading = false
      })
    },
  }

}
</script>